<template>
  <v-dialog v-model="dialog" max-width="900" persistent>
    <v-card>
      <v-card-title class="text-h5 primary white--text">
        <v-icon left color="white">mdi-account-multiple</v-icon>
        Datos de Firmantes Municipales
      </v-card-title>

      <v-card-text class="pt-6">
        <!-- Instrucciones -->
        <v-alert type="info" dense text class="mb-4">
          Complete los datos de ambos firmantes que aparecerán en los certificados PDF.
        </v-alert>

        <!-- FIRMANTE 1: Administrador/Secretario Municipal -->
        <v-subheader class="px-0 mb-2">
          <v-icon left color="primary">mdi-account-tie</v-icon>
          <strong>Firmante 1: Secretario Municipal</strong>
        </v-subheader>

        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="formData.firmante1_rut"
              label="RUT * - Enter para confirmar"
              outlined
              dense
              :disabled="buscandoFirmante1"
              :loading="buscandoFirmante1"
              :rules="[rules.required, rules.rut]"
              hint="Ej: 12.345.678-9"
              persistent-hint
              @input="formatearRutLimpio('firmante1_rut')"
              @keyup.enter="buscarPersonaPorRut('firmante1_rut')"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.firmante1_nombre"
              label="Nombre Completo *"
              outlined
              dense
              disabled
              :loading="buscandoFirmante1"
              :rules="[rules.required]"
              hint="Ej: Juan Pérez García"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="formData.firmante1_cargo"
              disabled
              label="Cargo *"
              outlined
              dense
              :rules="[rules.required]"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-checkbox
              v-model="formData.firmante1_subrogante"
              label="Subrogante"
              hide-details
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- FIRMANTE 2: Director DAF/RRHH -->
        <v-subheader class="px-0 mb-2">
          <v-icon left color="primary">mdi-account-tie</v-icon>
          <strong>Firmante 2: Director DAF o RRHH</strong>
        </v-subheader>

        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="formData.firmante2_rut"
              label="RUT * - Enter para confirmar"
              outlined
              dense
              :disabled="buscandoFirmante2"
              :loading="buscandoFirmante2"
              :rules="[rules.required, rules.rut]"
              hint="Ej: 23.456.789-0"
              persistent-hint
              @input="formatearRutLimpio('firmante2_rut')"
              @keyup.enter="buscarPersonaPorRut('firmante2_rut')"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.firmante2_nombre"
              label="Nombre Completo *"
              outlined
              dense
              disabled
              :loading="buscandoFirmante2"
              :rules="[rules.required]"
              hint="Ej: María González López"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="formData.firmante2_cargo"
              label="Cargo *"
              outlined
              dense
              :rules="[rules.required]"
              hint="Ej: Director DAF"
              persistent-hint
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-checkbox
              v-model="formData.firmante2_subrogante"
              label="Subrogante"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cerrar">Cancelar</v-btn>
        <v-btn
          color="primary"
          :loading="guardando"
          :disabled="!formularioValido"
          @click="guardar"
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar Firmantes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { apiClient } from '@/admin/services/API'
import { notificar } from '@/admin/utils/notificar'

export default {
  name: 'ModalFirmantes',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    certificadoId: {
      type: Number,
      required: true,
    },
    firmantesActuales: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      guardando: false,
      buscandoFirmante1: false,
      buscandoFirmante2: false,
      formData: {
        firmante1_nombre: '',
        firmante1_rut: '',
        firmante1_cargo: 'Secretario Municipal',
        firmante1_subrogante: false,
        firmante2_nombre: '',
        firmante2_rut: '',
        firmante2_cargo: '',
        firmante2_subrogante: false,
      },
      rules: {
        required: v => !!v || 'Campo requerido',
        rut: v => {
          if (!v) return true
          const rutRegex = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/
          return rutRegex.test(v) || 'RUT inválido (formato: XX.XXX.XXX-X)'
        },
      },
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    formularioValido() {
      return (
        this.formData.firmante1_nombre &&
        this.formData.firmante1_rut &&
        this.formData.firmante1_cargo &&
        this.formData.firmante2_nombre &&
        this.formData.firmante2_rut &&
        this.formData.firmante2_cargo &&
        this.rules.rut(this.formData.firmante1_rut) === true &&
        this.rules.rut(this.formData.firmante2_rut) === true
      )
    },
  },
  watch: {
    firmantesActuales: {
      immediate: true,
      handler(val) {
        if (val) {
          this.formData = {
            firmante1_nombre: val.firmante1_nombre || '',
            firmante1_rut: val.firmante1_rut || '',
            firmante1_cargo: 'Secretario Municipal',
            firmante1_subrogante: val.firmante1_subrogante || false,
            firmante2_nombre: val.firmante2_nombre || '',
            firmante2_rut: val.firmante2_rut || '',
            firmante2_cargo: val.firmante2_cargo || '',
            firmante2_subrogante: val.firmante2_subrogante || false,
          }
        }
      },
    },
  },
  methods: {
    formatearRut(campo) {
      // Limpiar y formatear RUT automáticamente
      this.$nextTick(() => {
        let rut = this.formData[campo].replace(/[^\dkK]/g, '')
        if (rut.length >= 2) {
          const dv = rut.slice(-1)
          const numero = rut.slice(0, -1)
          rut = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + dv.toUpperCase()
          this.formData[campo] = rut
        }
      })
    },
    formatearRutLimpio(firmante) {
      this.formatearRut(firmante === 'firmante1_rut' ? 'firmante1_rut' : 'firmante2_rut')
    },
    async buscarPersonaPorRut(firmante) {
      try {
        if (firmante === 'firmante1_rut') {
          this.buscandoFirmante1 = true
        } else {
          this.buscandoFirmante2 = true
        }

        const campo = firmante === 'firmante1_rut' ? 'firmante1_rut' : 'firmante2_rut'
        const nuevoRut = this.formData[campo]

        // Limpiar el RUT para enviarlo sin formato
        const rutLimpio = nuevoRut.replace(/\./g, '')

        if(this.formData.firmante1_rut === this.formData.firmante2_rut) {
          notificar({ message: 'El RUT ingresado ya está asignado al otro firmante. Por favor, ingrese un RUT diferente', severity: 'error' })
          return
        }

        if (!nuevoRut || this.rules.rut(nuevoRut) !== true) {
          notificar({ message: 'RUT inválido. Por favor, ingrese un RUT en formato XX.XXX.XXX-X', severity: 'error' })
          return
        }

        const { data } = await apiClient.get(`/tramitesgenerales/informacion-personal/${rutLimpio}`)

        if (firmante === 'firmante1_rut') {
          this.formData.firmante1_nombre = (data.nombres || '') + ' ' + (data.apellido_paterno || '') + ' ' + (data.apellido_materno || '')
        } else {
          this.formData.firmante2_nombre = (data.nombres || '') + ' ' + (data.apellido_paterno || '') + ' ' + (data.apellido_materno || '')
        }

      } catch (error) {
        if (error.response && error.response.data && error.response.data.message && error.response.data.message.includes('Error al consultar información del Registro Civil')) {
          notificar({ message: 'Error de comunicación con el Registro Civil. Por favor, intente nuevamente más tarde.', severity: 'error' })
        } else {
          notificar({ message: 'No se encontró información para el RUT ingresado. Por favor, verifique el RUT.', severity: 'error' })
        }
      } finally {
        this.buscandoFirmante1 = false
        this.buscandoFirmante2 = false
      }
    },
    async guardar() {
      if (!this.formularioValido) return

      try {
        this.guardando = true
        await this.$emit('guardar', this.formData)
      } finally {
        this.guardando = false
      }
    },
    cerrar() {
      this.dialog = false
    },
  },
}
</script>

<style scoped>
/* Oscurece el label cuando está vacío */
::v-deep .v-input:not(.v-input--is-disabled) .v-label {
  color: rgba(0, 0, 0) !important;
}
</style>
